import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import {
  ArrowLeft,
  Loader2,
  CreditCard,
  Receipt,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

export default function GuestFolioPage() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [folio, setFolio] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    loadFolio();
  }, [isAuthenticated, authLoading, reservationId]);

  const loadFolio = async () => {
    try {
      setLoading(true);
      const response = await guestPortalApi.getFolio(token, reservationId);
      setFolio(response.data);
    } catch (error) {
      console.error('Failed to load folio:', error);
      toast.error('Failed to load folio details');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="guest-folio-page">
      <Button variant="ghost" className="mb-6" onClick={() => navigate(`/portal/reservations/${reservationId}`)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Reservation
      </Button>

      <div className="flex items-center gap-3 mb-8">
        <Receipt className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Folio Details</h1>
          <p className="text-muted-foreground">View your charges and payments</p>
        </div>
      </div>

      {!folio || (Array.isArray(folio) && folio.length === 0) ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No Folio Available</p>
            <p className="text-muted-foreground">Folio details will be available after check-in</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Total Charges</p>
                <p className="text-2xl font-bold">${(folio.total_charges || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <p className="text-2xl font-bold text-green-600">${(folio.total_payments || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-sm text-muted-foreground">Balance Due</p>
                <p className="text-2xl font-bold text-orange-600">${(folio.balance || 0).toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Transactions */}
          {folio.transactions && folio.transactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {folio.transactions.map((tx, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{tx.date || tx.created_at?.split('T')[0]}</TableCell>
                        <TableCell>{tx.description}</TableCell>
                        <TableCell>
                          <Badge variant={tx.type === 'PAYMENT' ? 'default' : 'outline'}>
                            {tx.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {tx.type === 'PAYMENT' ? '-' : ''}${Math.abs(tx.amount || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
